import React, { useEffect, useRef, useState } from 'react';
import './index.scss';
import DeafaultGame from '@/pages/home/assets/img/common/game-default.svg'
import moment from 'moment';
import TimeLineIcon from './TimeLineIcon';
import { formatCompactDateString } from '@sg/utils/common';
import { sanitizeRichHtml } from '@sg/utils/sanitizeHtml';
import { getTimelineDateRange, getTargetYearMonth } from './utils';
import { addProjectToMktCostCalendar } from '@sg/services';
import {
  Bubble,
  Select,
  Input,
  Modal,
} from 'tea-component';
import FollowerRange from '@sg/components/FollowerRange';
import MobileIcon from '@/pages/strategic/assests/svg/mobile.svg';
import PcIcon from '@/pages/strategic/assests/svg/pc.svg';
import ConsoleIcon from '@/pages/strategic/assests/svg/console.svg';
import AddIcon from '@/pages/strategic/assests/svg/add-icon.svg';
import DragHandle from '@sg/assests/svg/draghandle-icon.svg';
import DeleteButton from '../DeleteButton';
// @ts-ignore
import * as rbd from 'react-beautiful-dnd';
import AutoTextarea from '@gp/components/AutoTextareaNew';

interface TimelineProps {
  options?: any[]; // For quarterly variant: [{ title: "Q1", data: [...] }]
  cards?: any[]; // For monthly variant: [{ id, date, project, status, remark, ... }]
  variant?: 'quarterly' | 'monthly'; // Layout variant
  projectOptions?: any[]; // Project options for dropdown
  loading?: boolean; // Loading state for project options

  // Edit mode props
  isEditing?: boolean;
  canEdit?: boolean;

  // Display options
  hideStudio?: boolean; // Hide the studio field in cards

  // Card management callbacks
  onCardsChange?: (cards: any[]) => void; // Callback for all card updates (add/edit/reorder) - used with cards prop
  onDeleteCard?: (cardId: number | string) => Promise<boolean>; // Async callback for deleting cards (returns true if successful)
  onProjectSearch?: (searchTerm: string) => void; // Callback for server-side project search
}

const Timeline = React.forwardRef<any, TimelineProps>(({
  options,
  cards,
  variant = 'quarterly',
  projectOptions = [],
  loading = false,
  isEditing = false,
  canEdit = false,
  hideStudio = false,
  onCardsChange,
  onDeleteCard,
  onProjectSearch
}, ref) => {
  const isMonthly = variant === 'monthly';
  const timelineRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnce = useRef(false); // Track if initial scroll has happened
  const [stickyYearLabel, setStickyYearLabel] = useState<number | null>(null);
  const now = moment();
  const currentMonth = now.month();
  const currentYear = now.year();

  // Convert flat cards array to grouped structure for display
  const displayOptions = React.useMemo(() => {
    if (options) {
      // Use options for quarterly variant (no editing, no drag and drop)
      // console.log('Using options (grouped structure for quarterly):', options);
      return options;
    }

    if (cards !== undefined) {
      // Group flat array by month for monthly variant (with editing and drag and drop)
      const { monthsWithYears } = getTimelineDateRange();

      const grouped = monthsWithYears.map((monthInfo) => {
        const monthKey = `${monthInfo.year}-${monthInfo.monthIndex.toString().padStart(2, '0')}`;
        const monthCards = cards.filter((card: any) => {
          const cardDate = moment(card.date);
          const cardYear = cardDate.year();
          const cardMonth = cardDate.month() + 1; // moment().month() is 0-indexed
          return cardYear === monthInfo.year && cardMonth === monthInfo.monthIndex;
        });

        return {
          title: monthInfo.title,
          data: monthCards,
          icon: TimeLineIcon,
          year: monthInfo.year,
          monthIndex: monthInfo.monthIndex,
          monthKey,
        };
      });

      // console.log('Grouped displayOptions:', grouped);
      return grouped;
    }

    // console.log('No options or cards provided, returning empty array');
    return [];
  }, [options, cards]);

  // State for edit mode values
  const [editValues, setEditValues] = useState<{ [key: string]: { status: string; remark: string; project: string } }>({});

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: { project?: boolean; status?: boolean } }>({});


  // Status options for dropdown
  const statusOptions = [
    { value: 'approved', text: 'Approved' },
    { value: 'pending', text: 'Pending' },
    { value: 'rejected', text: 'Rejected' },
    { value: '-', text: '-' },
  ];

  // Helper functions for edit mode
  const getEditValue = (cardId: string, field: 'status' | 'remark' | 'project') => {
    return editValues[cardId]?.[field] || '';
  };

  const updateEditValue = (cardId: string, field: 'status' | 'remark' | 'project', value: string) => {
    console.log('✏️ [TIMELINE] updateEditValue - User edited a field', {
      cardId,
      field,
      value,
      currentEditValues: editValues[cardId]
    });
    setEditValues(prev => ({
      ...prev,
      [cardId]: {
        ...prev[cardId],
        [field]: value
      }
    }));
  };

  const initializeEditValues = (cardId: string, status: string, remark: string, project: string = '') => {
    if (!editValues[cardId]) {
      setEditValues(prev => ({
        ...prev,
        [cardId]: { status, remark, project }
      }));
    }
  };

  // Function to add a new card with default placeholders
  const handleAddNewCard = (monthIndex: number, monthTitle: string) => {
    console.log('🟢 [TIMELINE] handleAddNewCard - User clicked Add button', {
      monthIndex,
      monthTitle,
      currentCardsCount: cards?.length || 0
    });

    // Get the actual year and month from displayOptions
    const monthData = displayOptions[monthIndex];
    if (!monthData) {
      console.error('🟢 [TIMELINE] Invalid monthIndex, no data found');
      return;
    }

    const newCardId = `new-card-${monthIndex}-${Date.now()}`;
    const targetYear = monthData?.year ?? getTargetYearMonth(monthIndex).targetYear;
    const targetMonth = monthData?.monthIndex ?? getTargetYearMonth(monthIndex).targetMonth;
    const newDate = `${targetYear}-${targetMonth.toString().padStart(2, '0')}-01`;

    const newCard = {
      id: newCardId,
      project: '',
      status: '',
      remark: '',
      cover: DeafaultGame,
      date: newDate,
      sort: 1, // Will be recalculated when saved
      game_id: '',
      isNew: true // Flag to identify newly created cards
    };

    console.log('🟢 [TIMELINE] Created new card object:', newCard, {
      targetYear,
      targetMonth,
      monthIndex,
      currentMonth
    });

    // Initialize editValues for the new card
    initializeEditValues(newCardId, '', '', '');
    console.log('🟢 [TIMELINE] Initialized editValues for new card');

    // With flat array design, add to beginning of cards array
    if (cards !== undefined && onCardsChange) {
      const updatedCards = [newCard, ...cards];
      console.log('🟢 [TIMELINE] Calling onCardsChange (parent setState)', {
        newCardsCount: updatedCards.length,
        operation: 'ADD'
      });
      onCardsChange(updatedCards);
    }
  };

  // Validation function for required fields
  const validateCards = () => {
    console.log('✅ [TIMELINE] validateCards - Starting validation', {
      editValuesCount: Object.keys(editValues).length,
      totalCardsCount: cards?.length || 0
    });

    const errors: { [key: string]: { project?: boolean; status?: boolean } } = {};
    let hasErrors = false;

    // With flat array design, just check all cards in displayOptions
    displayOptions.forEach((item: any) => {
      if (item.data && Array.isArray(item.data)) {
        item.data.forEach((card: any) => {
          const projectValue = getEditValue(card.id, 'project') || card.project || '';
          const statusValue = getEditValue(card.id, 'status') || card.status || '';

          console.log(`✅ [TIMELINE] Validating card ${card.id}:`, {
            hasEditValues: !!editValues[card.id],
            editValues: editValues[card.id],
            projectValue,
            statusValue,
            isNew: card.isNew
          });

          const cardErrors: { project?: boolean; status?: boolean } = {};

          // Only validate cards that have editValues (being edited or new)
          if (editValues[card.id]) {
            if (!projectValue.trim()) {
              console.log(`✅ [TIMELINE] Card ${card.id} - project is empty!`);
              cardErrors.project = true;
              hasErrors = true;
            }

            if (!statusValue.trim()) {
              console.log(`✅ [TIMELINE] Card ${card.id} - status is empty!`);
              cardErrors.status = true;
              hasErrors = true;
            }

            if (Object.keys(cardErrors).length > 0) {
              errors[card.id] = cardErrors;
              console.log(`✅ [TIMELINE] Card ${card.id} has validation errors:`, cardErrors);
            }
          } else {
            console.log(`✅ [TIMELINE] Card ${card.id} - skipping (no editValues)`);
          }
        });
      }
    });

    setValidationErrors(errors);
    console.log('✅ [TIMELINE] Validation complete', {
      hasErrors,
      errorCount: Object.keys(errors).length,
      errors
    });
    return !hasErrors; // Return true if no errors, false otherwise
  };


  // Function to clear validation errors for a specific field
  const clearValidationError = (cardId: string, field: 'project' | 'status') => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[cardId]) {
        delete newErrors[cardId][field];
        if (Object.keys(newErrors[cardId]).length === 0) {
          delete newErrors[cardId];
        }
      }
      return newErrors;
    });
  };

  // Function to save editValues back to cards
  const saveCards = () => {
    console.log('💾 [TIMELINE] saveCards - Merging editValues into cards', {
      editValuesCount: Object.keys(editValues).length,
      editValues,
      currentCardsCount: cards?.length || 0
    });

    if (cards && onCardsChange) {
      const updatedCards = cards.map((card: any) => {
        // If this card has editValues, merge them into the card
        if (editValues[card.id]) {
          const mergedCard = {
            ...card,
            project: editValues[card.id].project || card.project,
            status: editValues[card.id].status || card.status,
            remark: editValues[card.id].remark || card.remark,
            isNew: false // Remove isNew flag after saving
          };
          console.log('💾 [TIMELINE] Merged card:', {
            cardId: card.id,
            before: card,
            after: mergedCard
          });
          return mergedCard;
        }
        return card;
      });

      console.log('💾 [TIMELINE] Calling onCardsChange (parent setState) with merged data', {
        updatedCardsCount: updatedCards.length,
        operation: 'SAVE'
      });
      onCardsChange(updatedCards);

      // Clear editValues after saving
      setEditValues({});
      console.log('💾 [TIMELINE] Cleared editValues');

      // Return the updated cards for immediate use
      return updatedCards;
    }

    return cards || [];
  };

  // Drag and drop handler
  const handleDragEnd = (result: any) => {
    const { destination, source, draggableId } = result;

    console.log('🔄 [TIMELINE] handleDragEnd - Drag and drop event', {
      draggableId,
      source,
      destination
    });

    // If dropped outside a droppable area
    if (!destination) {
      console.log('🔄 [TIMELINE] Dropped outside - ignoring');
      return;
    }

    // If dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      console.log('🔄 [TIMELINE] Dropped in same position - ignoring');
      return;
    }

    const sourceMonthIndex = parseInt(source.droppableId, 10);
    const destMonthIndex = parseInt(destination.droppableId, 10);

    const sourceMonthInfo = displayOptions[sourceMonthIndex];
    const destMonthInfo = displayOptions[destMonthIndex];

    if (!sourceMonthInfo || !destMonthInfo) {
      console.log('🔄 [TIMELINE] Invalid source/destination month info');
      return;
    }

    const sourceMonthKey = sourceMonthInfo.monthKey;
    const destMonthKey = destMonthInfo.monthKey;

    const getCardMonthKey = (card: any) => {
      const date = moment(card.date);
      if (!date.isValid()) {
        return '';
      }
      return date.format('YYYY-MM');
    };

    const groupCardsByMonth = (cardsList: any[]) => {
      return cardsList.reduce<Record<string, any[]>>((acc, card) => {
        const key = getCardMonthKey(card);
        if (!key) {
          return acc;
        }
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(card);
        return acc;
      }, {});
    };

    console.log('🔄 [TIMELINE] Drag details:', {
      sourceMonthIndex,
      destMonthIndex,
      sourceIndex: source.index,
      destIndex: destination.index
    });

    // If using flat array structure
    if (cards && onCardsChange) {
      console.log('🔄 [TIMELINE] Using flat array drag logic');
      const monthTitles = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      const monthKeyFromDate = (date: string) => moment(date).format('YYYY-MM');
      const monthOrderKeys = displayOptions.map((option: any) => option.monthKey);
      const buildCardsByMonth = (cardList: any[]) => {
        const map: Record<string, any[]> = {};
        monthOrderKeys.forEach((key: string) => {
          map[key] = [];
        });
        cardList.forEach((card: any) => {
          const key = monthKeyFromDate(card.date);
          if (!map[key]) {
            map[key] = [];
          }
          map[key].push(card);
        });
        return map;
      };

      // Get source and dest month cards from displayOptions
      const sourceCards = displayOptions[sourceMonthIndex]?.data || [];
      const destCards = displayOptions[destMonthIndex]?.data || [];

      console.log('🔄 [TIMELINE] Source and dest cards:', {
        sourceCards,
        destCards,
        sourceCardsCount: sourceCards.length,
        destCardsCount: destCards.length
      });

      // Find the card being moved - handle both string and number IDs
      const movedCard = sourceCards.find((card: any) => String(card.id) === String(draggableId));
      console.log('🔄 [TIMELINE] Found card to move:', movedCard, {
        draggableId,
        draggableIdType: typeof draggableId,
        lookingFor: String(draggableId)
      });
      if (!movedCard) {
        console.log('🔄 [TIMELINE] Card not found in source - aborting');
        return;
      }

      // Backend uses array order, not sort field!
      // Strategy: Rebuild the entire array in the correct order

      console.log('🔄 [TIMELINE] Rebuilding array in correct order');

      if (sourceMonthIndex === destMonthIndex) {
        console.log('🔄 [TIMELINE] Same month drag - reorder within month');

        const cardsByMonth = buildCardsByMonth(cards);
        const sourceMonthKey = displayOptions[sourceMonthIndex]?.monthKey;
        const currentMonthCards = [...(cardsByMonth[sourceMonthKey] || [])];

        currentMonthCards.splice(source.index, 1);
        currentMonthCards.splice(destination.index, 0, movedCard);

        console.log('🔄 [TIMELINE] Reordered month cards:', currentMonthCards);

        cardsByMonth[sourceMonthKey] = currentMonthCards;

        const updatedCards: any[] = [];
        monthOrderKeys.forEach((key: string) => {
          const monthCardsForKey = cardsByMonth[key] || [];
          if (monthCardsForKey.length > 0) {
            updatedCards.push(...monthCardsForKey);
          }
        });

        console.log('🔄 [TIMELINE] Rebuilt array with new order:', updatedCards);
        onCardsChange(updatedCards);
        return;
      } else {
        console.log('🔄 [TIMELINE] Cross-month drag - moving card to different month');

        // Different months - update date and rebuild array
        const destMonthInfo = displayOptions[destMonthIndex];
        const newDate = moment(movedCard.date)
          .set({
            year: destMonthInfo?.year,
            month: (destMonthInfo?.monthIndex || 1) - 1,
            date: 1,
          })
          .format('YYYY-MM-DD');

        // Update the moved card's date
        const updatedMovedCard = { ...movedCard, date: newDate };

        console.log('🔄 [TIMELINE] Card before update:', movedCard);
        console.log('🔄 [TIMELINE] Card after date update:', updatedMovedCard);

        const sourceMonthKey = displayOptions[sourceMonthIndex]?.monthKey;
        const destMonthKey = destMonthInfo?.monthKey;
        const cardsByMonth = buildCardsByMonth(cards);

        const sourceMonthCards = cardsByMonth[sourceMonthKey] || [];
        const destMonthCards = cardsByMonth[destMonthKey] || [];

        const sourceIndexInArray = sourceMonthCards.findIndex(
          (card: any) => String(card.id) === String(draggableId)
        );
        if (sourceIndexInArray >= 0) {
          sourceMonthCards.splice(sourceIndexInArray, 1);
        }

        const insertIndex = Math.min(destination.index, destMonthCards.length);
        // Ensure we only keep one instance per card ID within the destination month
        destMonthCards.splice(0, destMonthCards.length, ...destMonthCards.filter((card: any) => String(card.id) !== String(draggableId)));
        destMonthCards.splice(insertIndex, 0, updatedMovedCard);

        cardsByMonth[sourceMonthKey] = sourceMonthCards;
        cardsByMonth[destMonthKey] = destMonthCards;

        const updatedCards: any[] = [];
        monthOrderKeys.forEach((key: string) => {
          const monthCardsForKey = cardsByMonth[key] || [];
          if (monthCardsForKey.length > 0) {
            updatedCards.push(...monthCardsForKey);
          }
        });

        console.log('🔄 [TIMELINE] Rebuilt array with card moved to new month:', {
          finalCount: updatedCards.length,
          updatedCards
        });
        onCardsChange(updatedCards);
        return;
      }
    }
  };

  // Expose validation and save functions to parent component
  React.useImperativeHandle(ref, () => ({
    validateCards,
    saveCards,
    getEditValues: () => editValues, // Expose editValues for auto-save
    getValidationErrors: () => validationErrors, // Expose validation errors for auto-save
    setValidationErrors: (errors: { [key: string]: { project?: boolean; status?: boolean } }) => {
      setValidationErrors(errors);
    }, // Expose setValidationErrors to restore validation state
    setEditValues: (values: { [key: string]: { status: string; remark: string; project: string } }) => {
      setEditValues(values);
    } // Expose setEditValues to restore edit values state
  }));

  // Find the initial month index to show on first render.
  const getInitialMonthIndex = () => {
    if (displayOptions.length === 0) {
      return 0;
    }

    const previousMonthDate = now.clone().subtract(1, 'month');
    const previousMonthIndex = displayOptions.findIndex(
      (item: any) =>
        item?.year === previousMonthDate.year() &&
        item?.monthIndex === previousMonthDate.month() + 1,
    );
    if (previousMonthIndex >= 0) {
      return previousMonthIndex;
    }

    // Fallback: use current month if previous month is outside the timeline range.
    const currentMonthIndex = displayOptions.findIndex(
      (item: any) =>
        item?.year === currentYear && item?.monthIndex === currentMonth + 1,
    );
    return currentMonthIndex >= 0 ? currentMonthIndex : 0;
  };

  // Function to scroll to initial month.
  const scrollToPreviousMonth = () => {
    if (timelineRef.current && isMonthly) {
      const timelineContainer = timelineRef.current;
      // console.log("Timeline container found:", timelineContainer);

      if (timelineContainer) {
        const availableScrollWidth = timelineContainer.scrollWidth - timelineContainer.clientWidth;
        const monthWidth = timelineContainer.clientWidth / 4;
        const initialMonthIndex = getInitialMonthIndex();
        const targetScrollLeft = monthWidth * initialMonthIndex;
        const scrollPosition = Math.min(
          Math.max(targetScrollLeft, 0),
          Math.max(availableScrollWidth, 0),
        );

        // Apply the scroll
        timelineContainer.scrollLeft = scrollPosition;

        // Sync sticky year after programmatic scroll.
        if (monthWidth > 0) {
          const visibleMonthIndex = Math.min(
            displayOptions.length - 1,
            Math.max(0, Math.floor(scrollPosition / monthWidth)),
          );
          const visibleYear = displayOptions?.[visibleMonthIndex]?.year;
          if (visibleYear) {
            setStickyYearLabel(visibleYear);
          }
        }
      }
    }
  };

  // Scroll to previous month when component mounts and variant is monthly
  useEffect(() => {
    if (isMonthly && displayOptions.length > 0 && !hasScrolledOnce.current) {
      // Add a small delay to ensure the DOM is fully rendered
      const timer = setTimeout(() => {
        // console.log("Scrolling take place!!");
        scrollToPreviousMonth();
        hasScrolledOnce.current = true; // Mark as scrolled
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isMonthly, displayOptions]);

  // Update sticky year label based on current horizontal scroll position.
  useEffect(() => {
    if (!isMonthly || !timelineRef.current || displayOptions.length === 0) {
      return;
    }

    const timelineContainer = timelineRef.current;

    const updateStickyYear = () => {
      const monthWidth = timelineContainer.clientWidth / 4;
      if (monthWidth <= 0) {
        return;
      }

      const visibleMonthIndex = Math.min(
        displayOptions.length - 1,
        Math.max(0, Math.floor(timelineContainer.scrollLeft / monthWidth)),
      );
      const visibleYear = displayOptions?.[visibleMonthIndex]?.year;
      if (visibleYear) {
        setStickyYearLabel(visibleYear);
      }
    };

    // Set initial value and keep it in sync while scrolling.
    updateStickyYear();
    timelineContainer.addEventListener('scroll', updateStickyYear);
    return () => {
      timelineContainer.removeEventListener('scroll', updateStickyYear);
    };
  }, [isMonthly, displayOptions]);

  // Calculate year labels for timeline using utility function
  const yearLabels = React.useMemo(() => {
    const { firstYear, secondYear, secondYearPosition } = getTimelineDateRange();
    return { firstYear, secondYear, secondYearPosition };
  }, []);

  return (
    <div className={`ds-timeline ${isMonthly ? 'ds-timeline-monthly' : ''}`} ref={timelineRef}>
      {/* Fixed Year Label - Sticky */}
      {isMonthly && (
        <>
          <div className='ds-timeline-year-label'>
            {stickyYearLabel ?? yearLabels.firstYear}
          </div>
          {/* Gradient Fade Box - Separate sticky element */}
          <div className='ds-timeline-fade-box'></div>
        </>
      )}

      <div className='ds-timeline-header'>
        {displayOptions.map((item: any, index: number) => {
          // Check if this is the current month for enlarging the icon
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const isCurrentMonth =
            isMonthly &&
            item?.monthIndex === currentMonth + 1 &&
            item?.year === currentYear;

          // Check if we should show the second year label at this position
          const showSecondYear = isMonthly && index === yearLabels.secondYearPosition;

          return (
            <div className={`ds-timeline-item ${isCurrentMonth ? 'current-month' : ''}`} key={`${item.title}-${index}`}>
              {/* Second year label - positioned absolutely in timeline */}
              {showSecondYear && (
                <>
                  <div className='ds-timeline-year-label-absolute'>
                    {yearLabels.secondYear}
                  </div>
                  {/* Fade box for absolute year label */}
                  <div className='ds-timeline-fade-box-absolute'></div>
                </>
              )}
              <div className='ds-timeline-item-content'>
                {typeof item.icon === 'string' ? (
                  <img src={item.icon} alt="" />
                ) : (
                  // If icon is a component, render it with the title as label
                  React.createElement(item.icon, { label: item.title })
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className='ds-timeline-body'>
        <rbd.DragDropContext onDragEnd={handleDragEnd}>
          {displayOptions.map((item: any, index: number) => {
            return (
              <div className='ds-timeline-item' key={index}>
                {/* Add New Card Button for Edit Mode - Show first */}
                {isEditing && canEdit && (
                  <div
                    className='ds-timeline-add-card'
                    onClick={() => handleAddNewCard(index, item.title)}
                  >
                    <img src={AddIcon} alt="Add" className='ds-timeline-add-card-icon' />
                  </div>
                )}

                {/* Droppable area for this month */}
                <rbd.Droppable droppableId={index.toString()}>
                  {(provided: any, snapshot: any) => (
                    <div
                      className={`ds-timeline-droppable ${snapshot.isDraggingOver ? 'ds-timeline-droppable-active' : ''}`}
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {/* All Cards */}
                      {(() => {
                        // With flat array design, just use item.data directly
                        const allCards = item.data || [];

                        if (allCards.length > 0) {
                          return allCards.map((card: any, cardIndex: number) => {
                            // Generate a unique key by combining card.id with month index and card index
                            // This prevents React warnings when backend data has duplicate IDs
                            const uniqueKey = `${index}-${card.id}-${cardIndex}`;
                            return (
                              <rbd.Draggable
                                key={uniqueKey}
                                draggableId={String(card.id)}
                                index={cardIndex}
                                isDragDisabled={!isEditing}
                              >
                              {(provided: any, snapshot: any) => (
                                <div
                                  className={`ds-timeline-item-content ${snapshot.isDragging ? 'ds-timeline-dragging' : ''} ${validationErrors[card.id] ? 'ds-timeline-error' : ''}`}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  {/* Drag Handle for Edit Mode */}
                                  {isEditing && canEdit && (
                                    <div className='ds-timeline-drag-handle' {...provided.dragHandleProps}>
                                      <img src={DragHandle} alt="Drag handle" />
                                    </div>
                                  )}
                                  <div className={`ds-timeline-item-box ${isMonthly ? '' : 'ds-timeline-item-box--centered'}`}>
                                      <div className='ds-timeline-item-title'>
                                        <img src={card?.cover || DeafaultGame} alt="" />
                                      </div>
                                      <div className='ds-timeline-item-body'>
                                        {/* Monthly variant */}
                                        {isMonthly ? (
                                          <>
                                            <div className='ds-timeline-item-body-title'>
                                              {/* Project field - editable for new cards, static for existing */}
                                              {(() => {
                                                const isNewCard = card.isNew === true;

                                                if (isNewCard) {
                                                  // New card - show FollowerRange for project selection
                                                  return (
                                                    <FollowerRange
                                                      className={`dbs-follower-range dbs-timeline-project-select ${validationErrors[card.id]?.project ? 'ds-timeline-error' : ''}`}
                                                      overlayClassName="dbs-follower-range"
                                                      value={(() => {
                                                        // Get the project name (stored in editValues or card data)
                                                        const projectName = getEditValue(card.id, 'project') || card.project || '';
                                                        if (!projectName) return '';

                                                        // Find matching option by text (project name)
                                                        const matchingOption = projectOptions.find((opt: any) => opt.text === projectName);
                                                        if (matchingOption) {
                                                          // Return the option's value (e.g., 'custom_121323' or 'c00003330')
                                                          return matchingOption.value;
                                                        }

                                                        // If no match found, it's a user-typed custom value, return as-is
                                                        return projectName;
                                                      })()}
                                                      onChange={async (value) => {
                                                        // Check if it's a custom value (not in projectOptions)
                                                        const selectedOption = projectOptions.find((opt: any) => opt.value === value || opt.text === value);
                                                        const isCustom = !selectedOption;

                                                        // Update project name in editValues - always use the display name (text)
                                                        const projectName = isCustom ? value : (selectedOption?.text || value);
                                                        updateEditValue(card.id, 'project', projectName);
                                                        clearValidationError(card.id, 'project');

                                                        // Track non-custom project selection for recent list
                                                        if (!isCustom && selectedOption?.actualGameId && selectedOption.actualGameId !== '0') {
                                                          try {
                                                            await addProjectToMktCostCalendar({ game_id: selectedOption.actualGameId });
                                                            // Refresh the recent project list after tracking
                                                            if (onProjectSearch) {
                                                              onProjectSearch(''); // Fetch top 10 recent projects
                                                            }
                                                          } catch (error) {
                                                            console.error('Failed to track project selection:', error);
                                                          }
                                                        }

                                                        // Update game_id and cover in the card data
                                                        if (cards && onCardsChange) {
                                                          const updatedCards = cards.map((c: any) => {
                                                            if (String(c.id) === String(card.id)) {
                                                              return {
                                                                ...c,
                                                                // Use actualGameId if available (for custom projects), otherwise use value
                                                                game_id: isCustom ? '0' : (selectedOption?.actualGameId || selectedOption?.value || c.game_id),
                                                                // Set cover to empty string if custom or if selected option has no cover
                                                                cover: isCustom ? '' : (selectedOption?.cover !== undefined ? selectedOption.cover : c.cover)
                                                              };
                                                            }
                                                            return c;
                                                          });
                                                          onCardsChange(updatedCards);
                                                        }
                                                      }}
                                                      onSearch={onProjectSearch} // Server-side search callback
                                                      options={projectOptions}
                                                      placeholder={loading ? 'Loading projects...' : 'Select Project'}
                                                      searchable
                                                      isCustomize={true}
                                                      customizePlaceholder="Enter custom project name"
                                                      appearance="default"
                                                      size="auto"
                                                      // boxStyle={{ width: '270px' }}
                                                      loading={loading}
                                                    />
                                                  );
                                                } else {
                                                  // Existing card - show project name as text
                                                  return <span>{card?.project}</span>;
                                                }
                                              })()}

                                          {/* Delete Button for Edit Mode - shared for both new and existing cards */}
                                          {isEditing && canEdit && (
                                            <DeleteButton
                                              onClick={async () => {
                                                console.log('🗑️ [TIMELINE] Delete button clicked', { cardId: card.id, isNew: card.isNew });

                                                // Check if this is a new card (not yet saved to backend)
                                                const isNewCard = card.isNew === true;

                                                // Check if card has any content (from card data or editValues)
                                                const currentProject = getEditValue(card.id, 'project') || card.project || '';
                                                const currentStatus = getEditValue(card.id, 'status') || card.status || '';
                                                const currentRemark = getEditValue(card.id, 'remark') || card.remark || '';
                                                const hasContent = currentProject || currentStatus || currentRemark;

                                                if (!hasContent) {
                                                  console.log('🗑️ [TIMELINE] Empty card - delete directly without confirmation');
                                                  // Empty card - just remove from state (no API call needed)
                                                  if (cards && onCardsChange) {
                                                    const updatedCards = cards.filter((c: any) => c.id !== card.id);
                                                    onCardsChange(updatedCards);
                                                  }
                                                } else {
                                                  console.log('🗑️ [TIMELINE] Card has content - show confirmation modal');
                                                  // Card has content - show confirmation modal
                                                  const confirmed = await Modal.confirm({
                                                    className: 'dbs-delete-confirm-modal',
                                                    message: `Are you sure you want to delete the project?`,
                                                    okText: 'Confirm',
                                                    cancelText: 'Cancel',
                                                  });

                                                  if (confirmed) {
                                                    if (isNewCard) {
                                                      // New card - just remove from state (no API call needed)
                                                      console.log('🗑️ [TIMELINE] New card confirmed - removing from state without API call');
                                                      if (cards && onCardsChange) {
                                                        const updatedCards = cards.filter((c: any) => c.id !== card.id);
                                                        onCardsChange(updatedCards);
                                                      }
                                                    } else {
                                                      // Existing card - call API to delete
                                                      console.log('🗑️ [TIMELINE] Existing card confirmed - calling API to delete');
                                                      if (onDeleteCard) {
                                                        const success = await onDeleteCard(card.id);
                                                        if (success && cards && onCardsChange) {
                                                          console.log('🗑️ [TIMELINE] API delete successful - removing from local state');
                                                          const updatedCards = cards.filter((c: any) => c.id !== card.id);
                                                          onCardsChange(updatedCards);
                                                        } else if (!success) {
                                                          console.log('🗑️ [TIMELINE] API delete failed - keeping card');
                                                        }
                                                      } else {
                                                        // No delete callback - just remove from state
                                                        console.log('🗑️ [TIMELINE] No onDeleteCard callback - removing from state only');
                                                        if (cards && onCardsChange) {
                                                          const updatedCards = cards.filter((c: any) => c.id !== card.id);
                                                          onCardsChange(updatedCards);
                                                        }
                                                      }
                                                    }
                                                  } else {
                                                    console.log('🗑️ [TIMELINE] User cancelled deletion');
                                                  }
                                                }
                                              }}
                                            />
                                          )}
                                        </div>
                                        <div className='ds-timeline-item-body-content'>
                                          {isEditing && canEdit ? (
                                            <>
                                              {/* Edit Mode - Status Dropdown */}
                                              <div
                                                className={`ds-timeline-item-body-content-status-wrapper ${validationErrors[card.id]?.status ? 'ds-timeline-error' : ''}`}
                                              >
                                                {(() => {
                                                  const rawStatus = getEditValue(card.id, 'status') || card?.status || '';
                                                  const currentStatus = rawStatus.trim().toLowerCase();
                                                  // console.log('🔄 [TIMELINE] Status dot render check:', {
                                                  //   cardId: card.id,
                                                  //   rawStatus,
                                                  //   trimmedStatus: currentStatus
                                                  // });
                                                  return currentStatus && currentStatus !== '-'
                                                    ? (
                                                      <div
                                                        className={`ds-timeline-item-body-content-status-dot status-${currentStatus}`}
                                                      ></div>
                                                    ) : null;
                                                })()}
                                                <Select
                                                  value={getEditValue(card.id, 'status') || card?.status?.toLowerCase()}
                                                  onChange={(value) => {
                                                    initializeEditValues(card.id, card?.status || '', card?.remark || '');
                                                    updateEditValue(card.id, 'status', value);
                                                    clearValidationError(card.id, 'status');
                                                  }}
                                                  options={statusOptions}
                                                  className={`ds-timeline-item-body-content-status-text ${validationErrors[card.id]?.status ? 'ds-timeline-error' : ''}`}
                                                  listWidth={250}
                                                  boxClassName='dbs-common-box'
                                                  placeholder="Select Status"
                                                />
                                              </div>
                                              {/* Edit Mode - Remark Input */}
                                              {/* This input textarea is not used now, because the textbox wont keep expanding to fit the content */}
                                              {/* <Input.TextArea
                                                value={getEditValue(card.id, 'remark') || card?.remark || ''}
                                                onChange={(value) => {
                                                  initializeEditValues(card.id, card?.status || '', card?.remark || '');
                                                  updateEditValue(card.id, 'remark', value);
                                                }}
                                                placeholder="Enter remark"
                                                className='ds-timeline-remark-input'
                                              /> */}
                                              {(() => {
                                                const editRemark = editValues[card.id]?.remark;
                                                const remarkValue = editRemark !== undefined ? editRemark : (card?.remark || '');
                                                return (
                                                  <AutoTextarea
                                                    key={card.id}
                                                    value={remarkValue}
                                                    onChange={(value: string) => {
                                                      initializeEditValues(card.id, card?.status || '', value);
                                                      updateEditValue(card.id, 'remark', value);
                                                    }}
                                                    placeholder="Please enter description"
                                                    minHeight={52}
                                                    // className='ds-timeline-remark-input'
                                                  />
                                                );
                                              })()}
                                            </>
                                          ) : (
                                            <>
                                              {/* View Mode - Status Display */}
                                              <div className='ds-timeline-item-body-content-status-wrapper'>
                                                {(() => {
                                                  const rawStatus = card?.status || '';
                                                  const currentStatus = rawStatus.trim().toLowerCase();
                                                  if (!currentStatus || currentStatus === '-') {
                                                    return null;
                                                  }
                                                  return (
                                                    <div
                                                      className={`ds-timeline-item-body-content-status-dot status-${currentStatus}`}
                                                    ></div>
                                                  );
                                                })()}
                                                <span className='ds-timeline-item-body-content-status-text'>
                                                  {card?.status ? card.status.charAt(0).toUpperCase() + card.status.slice(1).toLowerCase() : ''}
                                                </span>
                                              </div>
                                              {/* View Mode - Remark Display */}
                                              <div className='ds-timeline-item-body-content-desc'>
                                                {/* {card?.remark || (<span style={{ color: '#262832', WebkitTextFillColor: '#262832' }}> - </span>)} */}
                                                <span
                                                  dangerouslySetInnerHTML={{
                                                    __html: card?.remark?.trim()
                                                      ? sanitizeRichHtml(card.remark)
                                                      : '<span style="color:#262832;"> - </span>',
                                                  }}
                                                />
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </>
                                    ) : (
                                      <> {/* Quarterly variant */}
                                        <div className='ds-timeline-item-body-title'>
                                          <div className='ds-timeline-item-body-title-wrapper'>
                                            <Bubble content={card?.project}>
                                              <span className='ds-timeline-item-body-title-text' title={card?.project}>
                                                {card?.project}
                                              </span>
                                            </Bubble>
                                          </div>
                                          <div className='ds-timeline-platform-icons'>
                                            {card?.mgmt_platform?.includes('mobile') && (
                                              <img
                                                src={MobileIcon}
                                                alt="Mobile"
                                                className='ds-timeline-platform-icon'
                                              />
                                            )}
                                            {card?.mgmt_platform?.includes('pc') && (
                                              <img
                                                src={PcIcon}
                                                alt="PC"
                                                className='ds-timeline-platform-icon'
                                              />
                                            )}
                                            {card?.mgmt_platform?.includes('console') && (
                                              <img
                                                src={ConsoleIcon}
                                                alt="Console"
                                                className='ds-timeline-platform-icon'
                                              />
                                            )}
                                          </div>
                                        </div>
                                        <div className='ds-timeline-item-body-content'>
                                          {card?.milestone && (
                                            <Bubble content={card?.milestone}>
                                              <div className="ds-timeline-item-body-content-phase">
                                                {card?.milestone}
                                              </div>
                                            </Bubble>
                                          )}
                                            <div className='ds-timeline-item-body-content-date'>
                                              {formatCompactDateString(card?.date, Number(card?.start_date_confirm))}
                                            </div>
                                        </div>
                                        {!hideStudio && (
                                          <Bubble content={card?.studio}>
                                            <div title={card?.studio} className='ds-timeline-item-body-content-studio'>
                                              {card?.studio}
                                            </div>
                                          </Bubble>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  </div>
                                </div>
                              )}
                            </rbd.Draggable>
                            );
                          });
                        } else {
                          // Empty month - render a placeholder to allow dropping
                          if (isEditing) {
                            // In edit mode, render an invisible placeholder that can receive drops
                            return (
                              <div
                                className='ds-timeline-empty-droppable'
                                style={{ minHeight: '60px', width: '100%' }}
                              >
                                {/* Invisible placeholder for drag and drop */}
                              </div>
                            );
                          } else {
                            // In view mode, show "No Data"
                            return (
                              <div className='ds-timeline-item-content' style={{ justifyContent: 'center' , alignItems: 'center' }}>
                                No Data
                              </div>
                            );
                          }
                        }
                      })()}
                      {provided.placeholder}
                    </div>
                  )}
                </rbd.Droppable>
              </div>
            );
          })}
        </rbd.DragDropContext>
      </div>
    </div>
  );
});

Timeline.displayName = 'Timeline';

export default Timeline;
